import React from 'react';
import { Calendar, Info } from 'lucide-react';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import type { BankHoliday } from '../../../utils/types';

interface UpcomingBankHolidaysProps {
  bankHolidays: BankHoliday[] | any;
  isLoading?: boolean;
  maxDisplay?: number;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
};

const getDayOfWeek = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { weekday: 'short' });
};

const isUpcoming = (dateString: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const holidayDate = new Date(dateString);
  return holidayDate >= today;
};

const isToday = (dateString: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const holidayDate = new Date(dateString);
  holidayDate.setHours(0, 0, 0, 0);
  return holidayDate.getTime() === today.getTime();
};

export const UpcomingBankHolidays: React.FC<UpcomingBankHolidaysProps> = ({
  bankHolidays,
  isLoading,
  maxDisplay = 6,
}) => {
  // Handle both array response and paginated response
  const holidaysList = Array.isArray(bankHolidays) 
    ? bankHolidays 
    : (bankHolidays as any)?.results || [];

  if (isLoading) {
    return (
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">Bank Holidays</h4>
        </div>
        <div className="flex justify-center py-4">
          <LoadingSpinner size="sm" />
        </div>
      </div>
    );
  }

  // Filter and sort upcoming holidays
  const upcomingHolidays = holidaysList
    .filter((h: BankHoliday) => isUpcoming(h.date))
    .sort((a: BankHoliday, b: BankHoliday) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, maxDisplay);

  if (upcomingHolidays.length === 0) {
    return (
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">Bank Holidays</h4>
        </div>
        <p className="text-sm text-blue-700">
          No upcoming bank holidays for the rest of the year.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-5 h-5 text-blue-600" />
        <h4 className="font-semibold text-blue-900">Upcoming Bank Holidays</h4>
      </div>

      <div className="space-y-2 mb-3">
        {upcomingHolidays.map((holiday) => {
          const today = isToday(holiday.date);
          return (
            <div
              key={holiday.id}
              className={`flex items-center gap-3 p-2 rounded-lg ${
                today
                  ? 'bg-amber-100 border border-amber-200'
                  : 'bg-white border border-blue-100'
              }`}
            >
              {/* Date Box */}
              <div
                className={`flex flex-col items-center justify-center w-12 h-12 rounded-lg border flex-shrink-0 ${
                  today
                    ? 'bg-amber-200 border-amber-300'
                    : 'bg-blue-100 border-blue-200'
                }`}
              >
                <span
                  className={`text-xs font-medium ${
                    today ? 'text-amber-800' : 'text-blue-700'
                  }`}
                >
                  {getDayOfWeek(holiday.date)}
                </span>
                <span
                  className={`text-lg font-bold ${
                    today ? 'text-amber-900' : 'text-blue-800'
                  }`}
                >
                  {new Date(holiday.date).getDate()}
                </span>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p
                  className={`font-medium truncate ${
                    today ? 'text-amber-900' : 'text-gray-900'
                  }`}
                >
                  {holiday.title}
                  {today && (
                    <span className="ml-2 text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                      Today
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  {formatDate(holiday.date)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Note */}
      <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-100 rounded-lg p-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>
          Bank holidays are excluded when calculating working days for your holiday requests.
        </p>
      </div>
    </div>
  );
};

export default UpcomingBankHolidays;
